// ****************** fronto.js ******************
// A compiler for Fronto Sepc to Vega-Lite
// Author: Hyeok Kim @ Adobe AEL; Northwester
// Dependency: D3.js (v6), VegaEmbed(), Vega, Vega-Lite;

import { getDirectContext, cancelDirectContext } from "../editor/direct.js";

// vg Test
import { default as VegaImport } from "../vg/vega.js";
import { default as VegaLiteImport } from "../vg/vega-lite.js";
import { default as VegaEmbedImport } from "../vg/vega-embed.js";

import * as d3 from 'd3';
import './default.js';
import * as Fronto from "./utility.js";

import { compileFronto } from "./compile.js";

let vega = VegaImport;
let vegaLite = VegaLiteImport;
let vegaEmbed = VegaEmbedImport;

// register custom functions to Vega
vega.expressionFunction('roundN', function (n, d) {
  let b = Math.pow(10, d);
  return Math.round(n * b) / b;
});

vega.expressionFunction('dateParse', function (a) {
  return Date.parse(a);
});

vega.expressionFunction('hashString', function (a) {
  return a?.toString()?.hashString();
});

vega.expressionFunction('toStringD', function (a) {
  if (a.constructor.name === "Array") return this.map(d => d.toStringD()).join(',');
  else if (a.constructor.name === "Date" && a != "Invalid Date") {
    return a.getTime().toString();
  } else {
    return a.toString();
  }
});

vega.expressionFunction('includes', function (a, d) {
  return a.includes(d);
});

vega.expressionFunction('join', function (a, d) {
  return a.join(d);
});

export async function renderFronto(placeSelector, spec, config = {}) {
  // Fronto renderer for Vega-Lite
  // @placeSelector: CSS selector for add a visualization
  // @spec: a Fronto spec
  let compiled = await compileFronto(spec, config);
  // shortcuts
  if (document.querySelector(placeSelector)) {
    document.querySelector(placeSelector).innerHTML = "";
  }
  let viewSpec = compiled.view; // Vega or Vega-Lite spec depending on the layout.
  let annoSpec = compiled.annotation;
  let emphSpec = compiled.emphasis;
  let titleSpec = compiled.title;
  let tooltipDetail = compiled.tooltipDetail;
  let viewObject = {};
  let opt = { "renderer": "svg", "actions": { "export": false, "source": false, "compiled": false, "editor": false } };
  // callout related -css
  let calloutStyle = `
  svg[data-role="annotation-callout"] {
    z-index: 900;
  }
  annotation[data-role="internalAnnotation"] {
    z-index: 950;
  }
  `;
  let styleDef = document.createElement("style")
  styleDef.innerHTML = calloutStyle;
  document.getElementsByTagName("head")[0].append(styleDef);
  // tooltip generation
  function makeTooltipFunc(fixed) {
    if (fixed) {
      let style = `.vg-fixed-tooltip {
        visibility: hidden;
        padding: 8px;
        position: absolute;
        bottom: 0;
        left: 0;
        z-index: 20000;
        width: 100%;
        font-family: sans-serif;
        font-size: 11px;
        border-radius: 3px;
        box-shadow: 2px 2px 4px rgba(0, 0, 0, 0.1);
        background-color: rgba(255, 255, 255, 0.95);
        border: 1px solid #d9d9d9;
        color: black;
      }
      
      .vg-fixed-tooltip table {
        margin: 0 auto;
      }
      
      .vg-fixed-tooltip td.key{
        color: #808080;
        text-align: left;
        padding-right: 5px;
      }
      
      .vg-fixed-tooltip td.value{
        color: black;
        text-align: left;
        padding-left: 4px;
        max-height: 7em;
      }`;
      let styleDef = document.createElement("style")
      styleDef.innerHTML = style;
      document.getElementsByTagName("head")[0].append(styleDef);
      return function (handler, event, item, value) {
        if (event.vegaType !== "mouseout") { // mouseon/mouseover  --> show tooltip (no reposition required)
          let tooltipElement = document.querySelector(placeSelector + " .vg-fixed-tooltip"); // style dependency on `vega-embed-style.css`
          if (tooltipElement === null) {
            tooltipElement = document.createElement("div")
            tooltipElement.className = "vg-fixed-tooltip";
            document.querySelector(placeSelector).append(tooltipElement);
          }
          let inh = document.querySelector(placeSelector).scrollTop;
          tooltipElement.style.bottom = (-inh) + "px";
          tooltipElement.innerHTML = "";
          tooltipElement.style.visibility = "visible";
          let table = document.createElement("table");
          let tooltipData = item.tooltip;
          Object.keys(tooltipData).forEach(key => {
            let cellKey = document.createElement("td")
            cellKey.className = "key";
            cellKey.innerHTML = key;
            let cellValue = document.createElement("td");
            cellValue.className = "value";
            let marktype = item.ariaRoleDescription;
            let expression = tooltipDetail.expressions.filter(d => d.field === key && d.role === marktype);
            if (expression.length > 0) {
              cellValue.innerHTML = Fronto.evalTooltipExpression(expression[0].expression, tooltipData[key]);
            }
            else cellValue.innerHTML = tooltipData[key];
            let row = document.createElement("tr");
            row.appendChild(cellKey)
            row.appendChild(cellValue);
            table.appendChild(row);
          })
          tooltipElement.appendChild(table);
        } else { // mouseout --> remove tooltip
          let tooltipElement = document.querySelector(placeSelector + " .vg-fixed-tooltip");
          if (tooltipElement) {
            tooltipElement.style.visibility = "hidden";
            tooltipElement.innerHTML = "";
          }
        }
      }
    } else {
      let style = `.vg-on_mark-tooltip {
        visibility: hidden;
        padding: 8px;
        position: fixed;
        z-index: 20000;
        width: auto;
        font-family: sans-serif;
        font-size: 11px;
        border-radius: 3px;
        box-shadow: 2px 2px 4px rgba(0, 0, 0, 0.1);
        background-color: rgba(255, 255, 255, 0.95);
        border: 1px solid #d9d9d9;
        color: black;
      }
      
      .vg-on_mark-tooltip table {
        margin: 0 auto;
      }
      
      .vg-on_mark-tooltip td.key{
        color: #808080;
        text-align: left;
        padding-right: 5px;
      }
      
      .vg-on_mark-tooltip td.value{
        color: black;
        text-align: left;
        padding-left: 4px;
        max-height: 7em;
      }`;
      let styleDef = document.createElement("style")
      styleDef.innerHTML = style;
      document.getElementsByTagName("head")[0].append(styleDef);
      return function (handler, event, item, value) {
        if (event.vegaType !== "mouseout") { // mouseon/mouseover  --> show tooltip (no reposition required)
          let tooltipElement = document.querySelector("body .vg-on_mark-tooltip"); // style dependency on `vega-embed-style.css`
          if (tooltipElement === null) {
            tooltipElement = document.createElement("div")
            tooltipElement.className = "vg-on_mark-tooltip";
            document.body.append(tooltipElement);
          }
          tooltipElement.innerHTML = "";
          tooltipElement.style.visibility = "visible";
          let table = document.createElement("table");
          let tooltipData = item.tooltip;
          Object.keys(tooltipData).forEach(key => {
            let cellKey = document.createElement("td")
            cellKey.className = "key";
            cellKey.innerHTML = key;
            let cellValue = document.createElement("td");
            cellValue.className = "value";
            let marktype = item.ariaRoleDescription;
            let expression = tooltipDetail.expressions.filter(d => d.field === key && d.role === marktype);
            if (expression.length > 0) {
              cellValue.innerHTML = Fronto.evalTooltipExpression(expression[0].expression, tooltipData[key]);
            }
            else cellValue.innerHTML = tooltipData[key];
            let row = document.createElement("tr");
            row.appendChild(cellKey)
            row.appendChild(cellValue);
            table.appendChild(row);
          })
          tooltipElement.appendChild(table);
          tooltipElement.style.left = (event.clientX + 5) + "px";
          tooltipElement.style.top = (event.clientY + 5) + "px";
        } else { // mouseout --> remove tooltip
          let tooltipElement = document.querySelector("body .vg-on_mark-tooltip");
          if (tooltipElement) {
            tooltipElement.style.visibility = "hidden";
            tooltipElement.innerHTML = "";
          }
        }
      }
    }
  }
  opt.tooltip = makeTooltipFunc(viewSpec.fixTooltip)
  d3.select(placeSelector).append("div").attr("id", "title-" + viewSpec.name).attr("data-role", "fronto-title");
  d3.select(placeSelector).append("div").attr("id", "view-" + viewSpec.name).attr("data-role", "fronto-view");
  viewObject = await vegaEmbed(placeSelector + " #view-" + viewSpec.name, viewSpec, opt);

  function dblClickFunction(event) { getDirectContext(event, placeSelector + " #view-" + viewSpec.name, spec); }

  if (spec.interaction?.context !== undefined && spec.interaction?.context !== false) {
    let newRepalcer = function () {
      Fronto.textReplacement(placeSelector + " #view-" + viewSpec.name, compiled.textReplacer, dblClickFunction);
      Fronto.annotationGenerator(placeSelector, annoSpec.data.items.concat(annoSpec.nondata.items), viewSpec.name, false, false, dblClickFunction);
    }
    viewObject.view.addEventListener("wheel", () => {
      viewObject.view.removeEventListener("mousemove", newRepalcer)
      Fronto.textReplacement(placeSelector + " #view-" + viewSpec.name, compiled.textReplacer, dblClickFunction);
      Fronto.annotationGenerator(placeSelector, annoSpec.data.items.concat(annoSpec.nondata.items), viewSpec.name, false, false, dblClickFunction);
    })
    viewObject.view.addEventListener("mouseup", () => {
      viewObject.view.removeEventListener("mousemove", newRepalcer)
      Fronto.textReplacement(placeSelector + " #view-" + viewSpec.name, compiled.textReplacer, dblClickFunction);
      Fronto.annotationGenerator(placeSelector, annoSpec.data.items.concat(annoSpec.nondata.items), viewSpec.name, false, false, dblClickFunction);
    })
    viewObject.view.addEventListener("mousedown", () => {
      viewObject.view.addEventListener("mousemove", newRepalcer)
    })
  }
  if (spec.interaction?.zoompan !== undefined && spec.interaction?.zoompan !== false) {
    let newRepalcer = function () {
      console.log(placeSelector);
      Fronto.textReplacement(placeSelector + " #view-" + viewSpec.name, compiled.textReplacer, dblClickFunction);
      Fronto.annotationGenerator(placeSelector, annoSpec.data.items.concat(annoSpec.nondata.items), viewSpec.name, false, false, dblClickFunction);
    }
    viewObject.view.addEventListener("wheel", () => {
      viewObject.view.removeEventListener("mousemove", newRepalcer)
      Fronto.textReplacement(placeSelector + " #view-" + viewSpec.name, compiled.textReplacer, dblClickFunction);
      Fronto.annotationGenerator(placeSelector, annoSpec.data.items.concat(annoSpec.nondata.items), viewSpec.name, false, false, dblClickFunction);
    })
    viewObject.view.addEventListener("mouseup", () => {
      viewObject.view.removeEventListener("mousemove", newRepalcer)
      Fronto.textReplacement(placeSelector + " #view-" + viewSpec.name, compiled.textReplacer, dblClickFunction);
      Fronto.annotationGenerator(placeSelector, annoSpec.data.items.concat(annoSpec.nondata.items), viewSpec.name, false, false, dblClickFunction);
    })
    viewObject.view.addEventListener("mousedown", () => {
      viewObject.view.addEventListener("mousemove", newRepalcer)
    })
  }
  // add click event
  viewObject.view.addEventListener("dblclick", (event) => {
    dblClickFunction(event);
  })
  // alignment
  let viewDom = document.querySelector(placeSelector + " #view-" + viewSpec.name);
  document.querySelector(placeSelector).style.position = "relative";
  if (spec.layout?.align === "right") {
    viewDom.style.right = "100%";
  }
  // annotation calssification 
  let entireAnnotations = annoSpec.data.items.concat(annoSpec.nondata.items);
  let nonautoAnnotations = entireAnnotations.filter(d => !d.autoPositioning);
  let autoAnnotations = entireAnnotations.filter(d => d.autoPositioning);

  Fronto.copyLegend(placeSelector + " #view-" + viewSpec.name, compiled.textReplacer, viewSpec, dblClickFunction);
  Fronto.textReplacement(placeSelector + " #view-" + viewSpec.name, compiled.textReplacer, dblClickFunction);
  Fronto.annotationGenerator(placeSelector, nonautoAnnotations, viewSpec.name, false, false, dblClickFunction);
  Fronto.emphasisGenerator(placeSelector, emphSpec.items, viewSpec.name, dblClickFunction);
  Fronto.titleGenerator(placeSelector + " #title-" + viewSpec.name, titleSpec, viewSpec.name, dblClickFunction);
  if (autoAnnotations.length > 0) {
    let spaceGrid = Fronto.compileSpace("#view-" + viewSpec.name);
    autoAnnotations.forEach(annoItem => {
      spaceGrid.getContiguousEmptySpace().then((data) => {
        // if (isDebug) spaceGrid.visualizeContiguousEmptySpace(1);
        Fronto.annotationGenerator(placeSelector, [annoItem], viewSpec.name, data, true, dblClickFunction);
      });
    });
  }
  document.querySelector(placeSelector + " details")?.remove();
  if (document.querySelector(placeSelector + " .vega-embed.has-actions")) {
    document.querySelector(placeSelector + " .vega-embed.has-actions").className = document.querySelector(placeSelector + " .vega-embed.has-actions").className.replace(" has-actions", "")
  }
  return { compiled, viewObject };
};